import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GALLERY_CATEGORIES } from "@/lib/config";
import { api, resolveImage } from "@/lib/api";
import { PageHeader, PrimaryBtn } from "@/components/site/shared";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import { openGhost } from "@/components/site/GhostAI";
import { Bot, Play } from "lucide-react";

export default function BeforeAfterPage() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);

  const available = ["All", ...GALLERY_CATEGORIES.slice(1).filter((c) =>
    projects.some((p) => p.category === c || (c === "Residential" && p.property_type === "residential") || (c === "Commercial" && p.property_type === "commercial")))];

  const filtered = projects.filter((p) => {
    if (filter === "All") return true;
    if (filter === "Residential") return p.property_type === "residential";
    if (filter === "Commercial") return p.property_type === "commercial";
    return p.category === filter;
  });

  return (
    <div>
      <PageHeader overline="Work Gallery" title="Before & After Transformations"
        subtitle="Real results from real projects. Drag any slider to reveal the transformation." />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filters">
            {available.map((c) => (
              <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${filter === c ? "bg-[var(--red)] text-white" : "bg-[var(--surface)] text-[var(--navy)] hover:bg-gray-200"}`}>
                {c}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 text-[var(--text-muted)]">No projects in this category yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filtered.map((p) => (
                <div key={p.id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden card-shadow" data-testid={`gallery-item-${p.id}`}>
                  {p.type === "video" && p.video_id ? (
                    <video src={resolveImage(p.video_id)} controls preload="metadata" className="w-full aspect-video bg-black" />
                  ) : p.before_image && p.after_image ? (
                    <div className="aspect-[4/3]"><BeforeAfter before={p.before_image} after={p.after_image} alt={p.title} /></div>
                  ) : (
                    <img src={resolveImage(p.after_image || p.before_image)} alt={p.title} className="w-full aspect-[4/3] object-cover" />
                  )}
                  {(p.before_images?.length > 1 || p.after_images?.length > 1) && (
                    <div className="flex gap-1.5 p-2 overflow-x-auto bg-gray-50" data-testid={`gallery-thumbs-${p.id}`}>
                      {[...(p.before_images || []).slice(1), ...(p.after_images || []).slice(1)].map((im, i) => (
                        <img key={i} src={resolveImage(im.id)} alt={im.caption || ""} className="h-16 w-16 rounded object-cover flex-shrink-0" />
                      ))}
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <h3 className="font-head font-bold text-lg text-[var(--navy)] uppercase">{p.title}</h3>
                      {p.featured && <span className="text-[10px] bg-[var(--red)] text-white px-2 py-0.5 rounded-full font-bold">FEATURED</span>}
                    </div>
                    <p className="text-sm text-[var(--text-muted)] mt-1">{p.description}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                      <span className="capitalize">{p.property_type}</span>{p.category && <>· {p.category}</>}{p.city && <>· {p.city}</>}
                    </div>
                    <div className="flex gap-3 mt-3">
                      <Link to="/get-a-quote" className="text-sm font-semibold text-[var(--red)] hover:underline">Get a Quote</Link>
                      <button onClick={openGhost} className="text-sm font-semibold text-[var(--navy)] hover:underline flex items-center gap-1"><Bot className="w-4 h-4" /> Ask Ghost AI</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-12 text-center"><PrimaryBtn to="/get-a-quote">Get Your Free Quote</PrimaryBtn></div>
        </div>
      </section>
    </div>
  );
}
